import React, { forwardRef, PropsWithChildren } from 'react';
import { cx } from '@linaria/atomic';
import { AnimatePresence, motion } from 'motion/react';
import { PolymorphicComponentPropWithRef, PolymorphicRef } from '../../../type-utils';
import * as styles from '../accordion.styles';
import { useAccordionItemContext } from '../atoms/accordion-item';
import { useAccordionContext } from '../provider/accordion.provider';

type AccordionBodyProps<C extends React.ElementType> = PolymorphicComponentPropWithRef<C>;

type AccordionBodyComponent = <C extends React.ElementType = 'section'>(
  props: AccordionBodyProps<C>
) => React.ReactNode;

export const AccordionBody: AccordionBodyComponent = forwardRef(
  <C extends React.ElementType = 'section'>(
    { children, as, className, ...rest }: AccordionBodyProps<C>,
    ref: PolymorphicRef<C>
  ) => {
    const {
      isItemActive,
      getBodyId,
      onAnimationStart,
      onAnimationEnd,
      onAnimationComplete,
      getHeaderId,
      shouldRenderBody,
      variant,
    } = useAccordionContext();
    const { value } = useAccordionItemContext();
    const isOpen = isItemActive(value);
    const bodyVariantStyle = variant
      ? styles.bodyVariantStyles[variant as keyof typeof styles.bodyVariantStyles]
      : undefined;

    const Component = as ? motion[as as any] : motion.section;
    const conditionalAnimationProps = shouldRenderBody
      ? { animate: isOpen ? 'open' : 'collapsed' }
      : { animate: 'open', exit: 'collapsed' };

    return (
      <ConditionalAnimatePresence isOpen={isOpen} shouldRenderBody={shouldRenderBody}>
        <motion.section
          key='content'
          initial='collapsed'
          {...conditionalAnimationProps}
          variants={{
            open: {
              height: 'auto',
              ...(shouldRenderBody ? { display: 'block' } : {}),
              ...(variant !== 'location' && { opacity: 1 }),
            },
            collapsed: {
              height: 0,
              ...(shouldRenderBody ? { transitionEnd: { display: 'none' } } : {}),
              ...(variant !== 'location' && { opacity: 0 }),
            },
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          role='region'
          id={getBodyId(value)}
          onAnimationStart={onAnimationStart}
          onAnimationEnd={onAnimationEnd}
          onAnimationComplete={onAnimationComplete}
          aria-labelledby={getHeaderId(value)}
          ref={ref}
          className={cx('accordion-body', styles.bodyBaseStyle, bodyVariantStyle)}
        >
          <Component className={cx(styles.bodyContentStyle, className)} {...rest}>
            {children}
          </Component>
        </motion.section>
      </ConditionalAnimatePresence>
    );
  }
);

type ConditionalAnimatePresenceProps = PropsWithChildren<{ isOpen: boolean; shouldRenderBody?: boolean }>;

const ConditionalAnimatePresence = ({ isOpen, shouldRenderBody, children }: ConditionalAnimatePresenceProps) => {
  return shouldRenderBody ? <>{children}</> : <AnimatePresence initial={false}>{isOpen && children}</AnimatePresence>;
};
